import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Check, ChevronRight, Container, Filter, MapPin, Search,
  Settings, Truck as TruckIcon, Warehouse as WarehouseIcon, X,
} from 'lucide-react';
import { useUserProfile } from '../context/UserProfileContext';
import { useFleet } from '../hooks/useFleet';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { CustomDropdown } from '../components/ui/CustomDropdown';
import type { Truck, TruckStatus, Warehouse } from '../types/admin';
import { useFleetDefaultTruckTypes } from '../hooks/useFleetDefaults';

// ── Constants ────────────────────────────────────────────────────────────────

/** Legacy labels for backward compatibility with existing truck data. */
const LEGACY_TRUCK_TYPE_LABELS: Record<string, string> = {
  cargo_van: 'Cargo Van',
  sprinter_van: 'Sprinter Van',
  '12ft_box': '12ft Box Truck',
  '16ft_box': '16ft Box Truck',
  '20ft_box': '20ft Box Truck',
  '24ft_box': '24ft Box Truck',
  '26ft_box': '26ft Box Truck',
  flatbed: 'Flatbed',
  other: 'Other',
};


const TRUCK_STATUS_LABELS: Record<TruckStatus, string> = {
  active: 'Active',
  maintenance: 'Maintenance',
  inactive: 'Inactive',
};

type FleetView = 'warehouse' | 'all';
const VIEW_KEY = 'iomi_fleet_view';

// ── Status dot colors ────────────────────────────────────────────────────────

function statusDotColor(status?: TruckStatus): string {
  if (status === 'active') return 'bg-emerald-500';
  if (status === 'maintenance') return 'bg-amber-500';
  return 'bg-stone';
}

function statusBadgeClasses(status?: TruckStatus): string {
  if (status === 'active') return 'bg-emerald-50 text-emerald-700';
  if (status === 'maintenance') return 'bg-amber-50 text-amber-700';
  return 'bg-stone text-slate';
}

function locationBadgeClasses(loc?: string): string {
  if (loc === 'on_job') return 'bg-blue-50 text-blue-700';
  if (loc === 'shop') return 'bg-plum/10 text-plum';
  return 'bg-stone text-slate';
}

// ── Toast ────────────────────────────────────────────────────────────────────

function SuccessToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 2500);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50
                    flex items-center gap-2 px-4 py-2.5 rounded-xl bg-midnight text-white shadow-lg
                    animate-in slide-in-from-bottom-4 fade-in duration-200">
      <Check size={14} className="text-emerald-400 shrink-0" />
      <span className="text-[13px] font-medium">{message}</span>
    </div>
  );
}

// ── Quick-reassign bottom sheet ──────────────────────────────────────────────

interface ReassignSheetProps {
  truck: Truck;
  warehouses: Warehouse[];
  warehouseMap: Map<string, string>;
  onReassign: (truckId: string, newLocation: string) => Promise<void>;
  onClose: () => void;
}

function ReassignSheet({ truck, warehouses, warehouseMap, onReassign, onClose }: ReassignSheetProps) {
  const [saving, setSaving] = useState(false);

  const currentLabel = truck.currentLocation
    ? truck.currentLocation === 'on_job' ? 'On Job'
      : truck.currentLocation === 'shop' ? 'Shop'
        : warehouseMap.get(truck.currentLocation) ?? 'Unknown'
    : 'Unassigned';

  const activeWarehouses = useMemo(
    () => warehouses
      .filter(w => (w.status ?? 'active') === 'active')
      .sort((a, b) => a.name.localeCompare(b.name)),
    [warehouses],
  );

  async function handleSelect(newLocation: string) {
    if (newLocation === truck.currentLocation) { onClose(); return; }
    setSaving(true);
    await onReassign(truck.id, newLocation);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 max-h-[70vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-4" />

        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <p className="font-semibold text-small text-midnight truncate">{truck.name}</p>
            <p className="text-[12px] text-slate">Current: {currentLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors" aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">Move to</p>

        {saving ? (
          <div className="flex justify-center py-6"><LoadingSpinner size="sm" /></div>
        ) : (
          <ul className="flex flex-col gap-1">
            {activeWarehouses.map(wh => (
              <li key={wh.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(wh.id)}
                  className={`w-full text-left h-11 px-3 rounded-lg flex items-center gap-3 transition-colors duration-150
                    ${truck.currentLocation === wh.id
                      ? 'bg-plum/10 text-plum font-semibold'
                      : 'text-midnight hover:bg-mist active:bg-stone'}`}
                >
                  <WarehouseIcon size={15} className="shrink-0 text-slate" />
                  <span className="text-small truncate">{wh.name}</span>
                  {truck.currentLocation === wh.id && <Check size={14} className="ml-auto text-plum shrink-0" />}
                </button>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => handleSelect('on_job')}
                className={`w-full text-left h-11 px-3 rounded-lg flex items-center gap-3 transition-colors duration-150
                  ${truck.currentLocation === 'on_job'
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-midnight hover:bg-mist active:bg-stone'}`}
              >
                <TruckIcon size={15} className="shrink-0 text-slate" />
                <span className="text-small">On Job</span>
                {truck.currentLocation === 'on_job' && <Check size={14} className="ml-auto text-blue-700 shrink-0" />}
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => handleSelect('shop')}
                className={`w-full text-left h-11 px-3 rounded-lg flex items-center gap-3 transition-colors duration-150
                  ${truck.currentLocation === 'shop'
                    ? 'bg-plum/10 text-plum font-semibold'
                    : 'text-midnight hover:bg-mist active:bg-stone'}`}
              >
                <Settings size={15} className="shrink-0 text-slate" />
                <span className="text-small">Shop</span>
                {truck.currentLocation === 'shop' && <Check size={14} className="ml-auto text-plum shrink-0" />}
              </button>
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Truck mini-card (used in warehouse groups) ───────────────────────────────

function TruckMiniCard({ truck, onClick }: { truck: Truck; onClick: () => void }) {
  const descParts: string[] = [];
  if (truck.type) descParts.push(LEGACY_TRUCK_TYPE_LABELS[truck.type] ?? truck.type);
  if (truck.year) descParts.push(String(truck.year));
  if (truck.make) descParts.push(truck.make);
  if (truck.model) descParts.push(truck.model);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg
                 hover:bg-mist active:bg-stone transition-colors duration-150"
    >
      <span className={`shrink-0 w-2.5 h-2.5 rounded-full ${statusDotColor(truck.status)}`} />
      <div className="flex-1 min-w-0">
        <p className="text-small font-medium text-midnight truncate">{truck.name}</p>
        {descParts.length > 0 && (
          <p className="text-[12px] text-slate truncate">{descParts.join(' · ')}</p>
        )}
      </div>
      <ChevronRight size={14} className="shrink-0 text-slate" />
    </button>
  );
}

// ── Warehouse group card (for "By Warehouse" view) ───────────────────────────

interface WarehouseGroupProps {
  label: string;
  address?: string;
  icon: React.ReactNode;
  trucks: Truck[];
  badgeClass?: string;
  onTruckTap: (truck: Truck) => void;
}

function WarehouseGroup({ label, address, icon, trucks, badgeClass, onTruckTap }: WarehouseGroupProps) {
  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-stone flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-plum/10 flex items-center justify-center mt-0.5">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-small text-midnight">{label}</p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${badgeClass ?? 'bg-stone text-slate'}`}>
              {trucks.length} truck{trucks.length !== 1 ? 's' : ''}
            </span>
          </div>
          {address && (
            <div className="flex items-start gap-1 mt-0.5">
              <MapPin size={12} className="shrink-0 text-slate mt-0.5" />
              <p className="text-[12px] text-slate leading-snug">{address}</p>
            </div>
          )}
        </div>
      </div>
      {trucks.length > 0 ? (
        <ul className="divide-y divide-stone">
          {trucks.map(t => (
            <li key={t.id}>
              <TruckMiniCard truck={t} onClick={() => onTruckTap(t)} />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-slate text-center py-4">No trucks here</p>
      )}
    </div>
  );
}

// ── All Trucks row ───────────────────────────────────────────────────────────

function TruckRow({ truck, warehouseMap, onClick }: { truck: Truck; warehouseMap: Map<string, string>; onClick: () => void }) {
  const locationLabel = truck.currentLocation
    ? truck.currentLocation === 'on_job' ? 'On Job'
      : truck.currentLocation === 'shop' ? 'Shop'
        : warehouseMap.get(truck.currentLocation) ?? 'Unknown'
    : 'Unassigned';

  const descParts: string[] = [];
  if (truck.year) descParts.push(String(truck.year));
  if (truck.make) descParts.push(truck.make);
  if (truck.model) descParts.push(truck.model);

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-white border border-stone rounded-xl p-4 shadow-brand-sm
                 flex items-start gap-3
                 hover:border-plum/40 hover:shadow-brand-md active:bg-mist transition-all duration-150"
    >
      <div className="shrink-0 w-9 h-9 rounded-lg bg-plum/10 flex items-center justify-center mt-0.5">
        <TruckIcon size={17} className="text-plum" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-semibold text-small text-midnight leading-snug">{truck.name}</p>
          {truck.status && (
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusBadgeClasses(truck.status)}`}>
              {TRUCK_STATUS_LABELS[truck.status]}
            </span>
          )}
        </div>
        {descParts.length > 0 && (
          <p className="text-[13px] text-slate mt-0.5 leading-snug">{descParts.join(' ')}</p>
        )}
        {truck.type && (
          <p className="text-[11px] text-slate mt-0.5">{LEGACY_TRUCK_TYPE_LABELS[truck.type] ?? truck.type}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
          {truck.licensePlate && (
            <span className="text-[12px] text-slate font-mono">{truck.licensePlate}</span>
          )}
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${locationBadgeClasses(truck.currentLocation)}`}>
            <MapPin size={10} /> {locationLabel}
          </span>
        </div>
      </div>
      <ChevronRight size={16} className="shrink-0 text-slate mt-1" />
    </button>
  );
}

// ── Fleet Page Content (reusable, used by OperationsPage) ───────────────────

export function FleetContent({ orgId, onTruckCountChange, onSidebarContent, embedded }: { orgId: string; onTruckCountChange?: (count: number) => void; onSidebarContent?: (content: React.ReactNode) => void; embedded?: boolean }) {
  const navigate = useNavigate();
  const { activeOrg } = useUserProfile();
  const { trucks, warehouses, loading, error, updateTruckLocation } = useFleet(orgId);

  const isDispatcher = activeOrg?.role === 'dispatcher';
  const isAdmin = activeOrg?.role === 'admin';
  const isFieldLead = activeOrg?.role === 'field_lead';
  const canReassign = isDispatcher || isAdmin || isFieldLead;

  // View toggle with localStorage persistence
  const [view, setView] = useState<FleetView>(() => {
    const stored = localStorage.getItem(VIEW_KEY);
    return stored === 'all' ? 'all' : 'warehouse';
  });

  function switchView(v: FleetView) {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  }

  // All Trucks view filters
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<TruckStatus | ''>('');
  const [filterType, setFilterType] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const { items: fleetTruckTypes } = useFleetDefaultTruckTypes();
  const [showFilters, setShowFilters] = useState(false);
  const hasActiveFilters = filterStatus !== '' || filterType !== '' || filterLocation !== '';

  // Reassign state
  const [reassigningTruck, setReassigningTruck] = useState<Truck | null>(null);
  const [toastMessage, setToastMessage] = useState('');

  // Notify parent of truck count changes
  useEffect(() => {
    if (!loading) onTruckCountChange?.(trucks.length);
  }, [trucks.length, loading, onTruckCountChange]);

  // Provide sidebar content for desktop two-column layout
  // Must be before any early returns to satisfy Rules of Hooks
  useEffect(() => {
    onSidebarContent?.(
      <div className="flex flex-col gap-1 p-1 bg-mist rounded-lg">
        <button
          type="button"
          onClick={() => switchView('warehouse')}
          className={`h-8 px-3 rounded-md text-[13px] font-semibold transition-colors duration-150 text-left ${
            view === 'warehouse' ? 'bg-white text-midnight shadow-sm' : 'text-slate hover:text-midnight'
          }`}
        >
          By Warehouse
        </button>
        <button
          type="button"
          onClick={() => switchView('all')}
          className={`h-8 px-3 rounded-md text-[13px] font-semibold transition-colors duration-150 text-left ${
            view === 'all' ? 'bg-white text-midnight shadow-sm' : 'text-slate hover:text-midnight'
          }`}
        >
          All Trucks
        </button>
      </div>,
    );
  }, [onSidebarContent, view]);

  const warehouseMap = useMemo(() => new Map(warehouses.map(w => [w.id, w.name])), [warehouses]);
  const activeWarehouses = useMemo(
    () => warehouses.filter(w => (w.status ?? 'active') === 'active').sort((a, b) => a.name.localeCompare(b.name)),
    [warehouses],
  );

  const handleTruckTap = useCallback((truck: Truck) => {
    if (canReassign) {
      setReassigningTruck(truck);
    }
  }, [canReassign]);

  const handleReassign = useCallback(async (truckId: string, newLocation: string) => {
    const truck = trucks.find(t => t.id === truckId);
    await updateTruckLocation(truckId, newLocation);
    const destLabel = newLocation === 'on_job' ? 'On Job'
      : newLocation === 'shop' ? 'Shop'
        : warehouseMap.get(newLocation) ?? 'Unknown';
    setToastMessage(`${truck?.name ?? 'Truck'} moved to ${destLabel}`);
  }, [trucks, updateTruckLocation, warehouseMap]);

  // ── By Warehouse view data ──────────────────────────────────────────────

  const warehouseGroups = useMemo(() => {
    const groups: { key: string; label: string; address?: string; icon: React.ReactNode; trucks: Truck[]; badgeClass?: string }[] = [];

    // Group by warehouse
    for (const wh of activeWarehouses) {
      groups.push({
        key: wh.id,
        label: wh.name,
        address: wh.address,
        icon: <WarehouseIcon size={17} className="text-plum" />,
        trucks: trucks.filter(t => t.currentLocation === wh.id),
      });
    }

    // Special groups
    const onJobTrucks = trucks.filter(t => t.currentLocation === 'on_job');
    const shopTrucks = trucks.filter(t => t.currentLocation === 'shop');
    const unassignedTrucks = trucks.filter(t => !t.currentLocation);

    if (onJobTrucks.length > 0) {
      groups.unshift({
        key: '_on_job',
        label: 'On Job',
        icon: <TruckIcon size={17} className="text-blue-600" />,
        trucks: onJobTrucks,
        badgeClass: 'bg-blue-50 text-blue-700',
      });
    }

    if (shopTrucks.length > 0) {
      groups.push({
        key: '_shop',
        label: 'Shop',
        icon: <Settings size={17} className="text-plum" />,
        trucks: shopTrucks,
        badgeClass: 'bg-plum/10 text-plum',
      });
    }

    if (unassignedTrucks.length > 0) {
      groups.push({
        key: '_unassigned',
        label: 'Unassigned',
        icon: <Container size={17} className="text-slate" />,
        trucks: unassignedTrucks,
        badgeClass: 'bg-stone text-slate',
      });
    }

    return groups;
  }, [trucks, activeWarehouses]);

  // ── All Trucks view data ────────────────────────────────────────────────

  const filteredTrucks = useMemo(() => {
    let list = [...trucks];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        t.name.toLowerCase().includes(q)
        || (t.make && t.make.toLowerCase().includes(q))
        || (t.model && t.model.toLowerCase().includes(q))
        || (t.licensePlate && t.licensePlate.toLowerCase().includes(q))
        || (t.year && String(t.year).includes(q))
      );
    }
    if (filterStatus) list = list.filter(t => t.status === filterStatus);
    if (filterType) list = list.filter(t => t.type === filterType);
    if (filterLocation) {
      if (filterLocation === '_unassigned') {
        list = list.filter(t => !t.currentLocation);
      } else {
        list = list.filter(t => t.currentLocation === filterLocation);
      }
    }
    // Sort: active first, then by name
    list.sort((a, b) => {
      const sa = a.status === 'active' ? 0 : a.status === 'maintenance' ? 1 : 2;
      const sb = b.status === 'active' ? 0 : b.status === 'maintenance' ? 1 : 2;
      if (sa !== sb) return sa - sb;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [trucks, search, filterStatus, filterType, filterLocation]);

  if (loading) {
    return (
      <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
    );
  }

  if (error) {
    const errorContent = (
      <div className="flex flex-col items-center py-16 gap-3 text-center">
        <Container size={36} className="text-rose" />
        <p className="text-small text-rose">{error}</p>
      </div>
    );
    if (embedded) return errorContent;
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
        <h1 className="font-display text-h1 text-midnight mb-1">Fleet</h1>
        {errorContent}
      </div>
    );
  }

  // Empty state — no trucks at all
  if (trucks.length === 0) {
    const emptyContent = (
      <div className="flex flex-col items-center py-16 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-mist flex items-center justify-center">
          <Container size={28} className="text-slate" />
        </div>
        <div>
          <p className="text-small font-semibold text-midnight mb-1">No trucks yet</p>
          <p className="text-[13px] text-slate">Add trucks in the Manage Fleet admin page to get started.</p>
        </div>
        {(activeOrg?.role === 'admin') && (
          <button
            type="button"
            onClick={() => navigate('/admin/operations/manage-fleet')}
            className="h-10 px-5 rounded-lg bg-plum text-white text-[13px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            Go to Manage Fleet
          </button>
        )}
      </div>
    );
    if (embedded) return emptyContent;
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
        <h1 className="font-display text-h1 text-midnight mb-1">Fleet</h1>
        <p className="text-[13px] text-slate mb-8">See where your trucks are and quickly reassign them.</p>
        {emptyContent}
      </div>
    );
  }

  const mainContent = (
    <>
      {/* View toggle — mobile only (desktop shows in sidebar) */}
      <div className="flex items-center gap-1 p-1 bg-mist rounded-lg mb-4 w-fit md:hidden">
        <button
          type="button"
          onClick={() => switchView('warehouse')}
          className={`h-8 px-3 rounded-md text-[13px] font-semibold transition-colors duration-150 ${
            view === 'warehouse' ? 'bg-white text-midnight shadow-sm' : 'text-slate hover:text-midnight'
          }`}
        >
          By Warehouse
        </button>
        <button
          type="button"
          onClick={() => switchView('all')}
          className={`h-8 px-3 rounded-md text-[13px] font-semibold transition-colors duration-150 ${
            view === 'all' ? 'bg-white text-midnight shadow-sm' : 'text-slate hover:text-midnight'
          }`}
        >
          All Trucks
        </button>
      </div>

      {/* ── By Warehouse View ─────────────────────────────────────────── */}
      {view === 'warehouse' && (
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2 md:gap-4">
          {warehouseGroups.map(g => (
            <WarehouseGroup
              key={g.key}
              label={g.label}
              address={g.address}
              icon={g.icon}
              trucks={g.trucks}
              badgeClass={g.badgeClass}
              onTruckTap={handleTruckTap}
            />
          ))}
        </div>
      )}

      {/* ── All Trucks View ───────────────────────────────────────────── */}
      {view === 'all' && (
        <>
          {/* Search */}
          <div className="relative mb-3">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, make, model, plate…"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-stone bg-white text-small text-midnight placeholder:text-slate focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20 transition-colors duration-150"
            />
            {search && (
              <button type="button" onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate hover:text-midnight"><X size={14} /></button>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 mb-3">
            <button
              type="button"
              onClick={() => setShowFilters(prev => !prev)}
              className={`flex items-center gap-1 text-[12px] font-semibold transition-colors duration-150 ${hasActiveFilters ? 'text-plum' : 'text-slate hover:text-midnight'}`}
            >
              <Filter size={13} /> Filters{hasActiveFilters ? ' (active)' : ''}
            </button>
            {hasActiveFilters && (
              <button type="button" onClick={() => { setFilterStatus(''); setFilterType(''); setFilterLocation(''); }} className="text-[12px] text-rose hover:underline">
                Clear
              </button>
            )}
          </div>

          {showFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              <CustomDropdown
                options={[
                  { value: '', label: 'All Statuses' },
                  ...(Object.entries(TRUCK_STATUS_LABELS) as [TruckStatus, string][]).map(([k, v]) => ({ value: k, label: v })),
                ]}
                value={filterStatus}
                onChange={v => setFilterStatus(v as TruckStatus | '')}
                placeholder="All Statuses"
                fullWidth={false}
                minWidth="120px"
              />
              <CustomDropdown
                options={[
                  { value: '', label: 'All Types' },
                  ...fleetTruckTypes.filter(t => t.active).map(t => ({ value: t.name, label: t.name })),
                ]}
                value={filterType}
                onChange={v => setFilterType(v)}
                placeholder="All Types"
                fullWidth={false}
                minWidth="120px"
              />
              <CustomDropdown
                options={[
                  { value: '', label: 'All Locations' },
                  ...activeWarehouses.map(wh => ({ value: wh.id, label: wh.name })),
                  { value: 'on_job', label: 'On Job' },
                  { value: 'shop', label: 'Shop' },
                  { value: '_unassigned', label: 'Unassigned' },
                ]}
                value={filterLocation}
                onChange={v => setFilterLocation(v)}
                placeholder="All Locations"
                fullWidth={false}
                minWidth="120px"
              />
            </div>
          )}

          {/* Truck list */}
          {filteredTrucks.length === 0 ? (
            <p className="text-small text-slate text-center py-8">No trucks match your search or filters.</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {filteredTrucks.map(t => (
                <li key={t.id}>
                  <TruckRow truck={t} warehouseMap={warehouseMap} onClick={() => handleTruckTap(t)} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {/* ── Reassign Bottom Sheet ──────────────────────────────────── */}
      {reassigningTruck && (
        <ReassignSheet
          truck={reassigningTruck}
          warehouses={warehouses}
          warehouseMap={warehouseMap}
          onReassign={handleReassign}
          onClose={() => setReassigningTruck(null)}
        />
      )}

      {/* ── Toast ──────────────────────────────────────────────────── */}
      {toastMessage && (
        <SuccessToast message={toastMessage} onDismiss={() => setToastMessage('')} />
      )}
    </>
  );

  if (embedded) return mainContent;

  return (
    <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-4xl md:mx-auto">
      {/* Header */}
      <div className="flex items-end justify-between mb-1">
        <h1 className="font-display text-h1 text-midnight">Fleet</h1>
        <p className="text-[13px] text-slate">{trucks.length} truck{trucks.length !== 1 ? 's' : ''}</p>
      </div>
      <p className="text-[13px] text-slate mb-4">
        {canReassign ? 'Tap a truck to quickly reassign its location.' : 'Fleet overview — truck locations and availability.'}
      </p>
      {mainContent}
    </div>
  );
}

// ── Page wrapper (guards feature flag + org access) ──────────────────────────

export function FleetPage() {
  const { activeOrg, loading } = useUserProfile();

  if (loading) {
    return (
      <div className="flex justify-center py-16"><LoadingSpinner size="lg" /></div>
    );
  }

  if (!activeOrg) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <p className="text-small text-slate">You don't have access to any organization.</p>
      </div>
    );
  }

  if (!(activeOrg.featureFleetManagement ?? false)) {
    return (
      <div className="px-4 pt-6 flex flex-col items-center py-20 gap-3 text-center">
        <Container size={36} className="text-stone" />
        <p className="text-small text-slate">Fleet Management is not enabled for your organization.</p>
      </div>
    );
  }

  return <FleetContent orgId={activeOrg.orgId} />;
}
